import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface Project {
  id: number;
  name: string;
  slug: string;
  description?: string;
  language: string;
  is_active: boolean;
  total_reels: number;
  published_reels: number;
}

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  fetchProjects: () => Promise<void>;
  selectProject: (project: Project | null) => void;
  createProject: (projectData: any) => Promise<Project>;
  updateProject: (id: number, projectData: any) => Promise<Project>;
  deleteProject: (id: number) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load saved project from localStorage
    const savedProjectId = localStorage.getItem('currentProjectId');
    if (savedProjectId && projects.length > 0) {
      const project = projects.find(p => p.id === parseInt(savedProjectId));
      if (project) {
        setCurrentProject(project);
      }
    }
  }, [projects]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await api.get('/projects/');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectProject = (project: Project | null) => {
    setCurrentProject(project);
    if (project) {
      localStorage.setItem('currentProjectId', project.id.toString());
    } else {
      localStorage.removeItem('currentProjectId');
    }
  };

  const createProject = async (projectData: any): Promise<Project> => {
    const response = await api.post('/projects/', projectData);
    const newProject = response.data;
    setProjects([...projects, newProject]);
    return newProject;
  };

  const updateProject = async (id: number, projectData: any): Promise<Project> => {
    const response = await api.patch(`/projects/${id}`, projectData);
    const updatedProject = response.data;
    setProjects(projects.map(p => p.id === id ? updatedProject : p));
    if (currentProject?.id === id) {
      setCurrentProject(updatedProject);
    }
    return updatedProject;
  };

  const deleteProject = async (id: number) => {
    await api.delete(`/projects/${id}`);
    setProjects(projects.filter(p => p.id !== id));
    if (currentProject?.id === id) {
      setCurrentProject(null);
    }
  };

  const value = {
    projects,
    currentProject,
    loading,
    fetchProjects,
    selectProject,
    createProject,
    updateProject,
    deleteProject,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};